import { useState, useRef, useEffect } from 'react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  addMonths, 
  subMonths,
  isSameMonth,
  isSameDay,
  parseISO,
} from 'date-fns';
import { CaretLeft, CaretRight, CalendarBlank } from '@phosphor-icons/react';

/**
 * Custom DatePicker component with full theme support
 * Replaces native <input type="date"> which doesn't respect theme colors
 */
export default function DatePicker({ 
  value, 
  onChange, 
  min,
  disabled = false,
  placeholder = 'Select date',
  className = '',
  error = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    return value ? parseISO(value) : new Date();
  });
  const containerRef = useRef(null);
  
  // Parse value to Date object
  const selectedDate = value ? parseISO(value) : null;
  const minDate = min ? parseISO(min) : null;
  
  // Update currentMonth when value changes externally
  useEffect(() => {
    if (value) {
      setCurrentMonth(parseISO(value));
    }
  }, [value]);
  
  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  
  // Close on Escape
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);
  
  // Generate calendar days
  const calendarDays = (() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const days = [];
    let day = startDate;
    
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    
    return days;
  })();
  
  function handleDateSelect(date) {
    const isDisabled = minDate && date < minDate;
    if (isDisabled) return;
    
    onChange(format(date, 'yyyy-MM-dd'));
    setIsOpen(false);
  }
  
  function handlePrevMonth(e) {
    e.stopPropagation();
    setCurrentMonth(prev => subMonths(prev, 1));
  }
  
  function handleNextMonth(e) {
    e.stopPropagation();
    setCurrentMonth(prev => addMonths(prev, 1));
  }
  
  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`input w-full text-left flex items-center justify-between gap-2 ${error ? 'input-error' : ''}`}
        style={{ 
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
        aria-invalid={error ? 'true' : 'false'}
      >
        <span style={{ color: selectedDate ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}>
          {selectedDate ? format(selectedDate, 'd MMM yyyy') : placeholder}
        </span>
        <CalendarBlank size={18} style={{ color: 'var(--color-text-muted)' }} />
      </button>
      
      {/* Dropdown calendar */}
      {isOpen && (
        <div 
          className="absolute z-50 mt-1 rounded-sm shadow-lg overflow-hidden"
          style={{
            backgroundColor: 'var(--color-bg-canvas)',
            border: '1px solid var(--color-border-subtle)',
            minWidth: '280px',
          }}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between px-3 py-2"
            style={{ 
              backgroundColor: 'var(--color-bg-surface)',
              borderBottom: '1px solid var(--color-border-subtle)',
            }}
          >
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 rounded transition-colors"
              style={{ color: 'var(--color-text-secondary)' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-bg-raised)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <CaretLeft size={18} weight="bold" />
            </button>
            <span 
              className="font-medium text-sm"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 rounded transition-colors"
              style={{ color: 'var(--color-text-secondary)' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-bg-raised)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <CaretRight size={18} weight="bold" />
            </button>
          </div>
          
          {/* Day headers */}
          <div className="grid grid-cols-7 px-2 py-1" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
              <div 
                key={day} 
                className="text-center text-xs font-medium py-1"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1 p-2">
            {calendarDays.map((day, index) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              const isDisabled = minDate && day < minDate;
              
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDateSelect(day)}
                  disabled={isDisabled}
                  className="w-8 h-8 rounded-sm text-sm font-medium transition-colors flex items-center justify-center"
                  style={{
                    backgroundColor: isSelected 
                      ? 'var(--color-brand-primary)' 
                      : 'transparent',
                    color: isSelected 
                      ? 'white' 
                      : isDisabled 
                        ? 'var(--color-text-muted)' 
                        : !isCurrentMonth 
                          ? 'var(--color-text-muted)' 
                          : 'var(--color-text-primary)',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.4 : 1,
                    outline: isToday && !isSelected ? '2px solid var(--color-brand-primary)' : 'none',
                    outlineOffset: '-2px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && !isDisabled) {
                      e.target.style.backgroundColor = 'var(--color-bg-raised)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
